"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  claimInvitation,
  completeOAuth,
  loginWithPassword,
  register,
  verifyMagicLink,
  verifyMfa,
} from "@/shared/auth/session-controller";
import { authKeys } from "@/shared/lib/query-keys";
import { authApi } from "./auth-api";

/** Email/password login. completeLogin (inside the controller) seeds identity. */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; password: string }) =>
      loginWithPassword(vars.email, vars.password),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; password: string; displayName?: string }) =>
      register(vars.email, vars.password, vars.displayName),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

/** Spend an invitation whose address has no account yet. The controller
 * runs completeLogin, so the caller is signed in and inside the org when
 * this resolves; the returned summary names it. */
export function useClaimInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { token: string; password: string; displayName?: string }) =>
      claimInvitation(vars.token, vars.password, vars.displayName),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useCompleteOAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { challengeId: string; token: string; signal?: AbortSignal }) =>
      completeOAuth(vars.challengeId, vars.token, vars.signal),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

/** Update mutable profile fields. Refetches /me for the canonical identity shape. */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { display_name: string }) => authApi.updateProfile(args),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (email: string) => authApi.forgotPassword(email) });
}

/** Second half of an MFA-gated login. completeLogin runs inside. */
export function useVerifyMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { challengeId: string; method: string; code: string }) =>
      verifyMfa(vars.challengeId, vars.method, vars.code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

/** Consume a magic link (the /magic landing page). */
export function useVerifyMagicLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { challengeId: string; token: string; signal?: AbortSignal }) =>
      verifyMagicLink(vars.challengeId, vars.token, vars.signal),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useSendMagicLink() {
  return useMutation({ mutationFn: (email: string) => authApi.sendMagicLink(email) });
}

export function useVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => authApi.verifyEmail(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useVerifyEmailLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { challenge_id: string; token: string }) => authApi.verifyEmailLink(vars),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useResendVerification() {
  return useMutation({ mutationFn: () => authApi.resendVerification() });
}

export function useChangeEmailStart() {
  return useMutation({
    mutationFn: (vars: { new_email: string; current_password: string }) =>
      authApi.changeEmailStart(vars),
  });
}

export function useChangeEmailConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => authApi.changeEmailConfirm(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: async (currentPassword: string) => {
      await authApi.deleteAccount(currentPassword);
      const { forceSignOut } = await import("@/shared/auth/session-controller");
      forceSignOut("explicit");
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (vars: { current_password: string; new_password: string }) =>
      authApi.changePassword(vars),
  });
}

// ── MFA management ─────────────────────────────────────────────────

export function useMfaEnroll() {
  return useMutation({
    mutationFn: (method: "totp" | "email" | "sms") => authApi.mfaEnroll(method),
  });
}

// ── Phone ──────────────────────────────────────────────────────────

export function usePhoneEnroll() {
  return useMutation({ mutationFn: (phone: string) => authApi.phoneEnroll(phone) });
}

export function usePhoneEnrollVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { challenge_id: string; code: string }) => authApi.phoneEnrollVerify(vars),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.me() }),
  });
}

export function usePhoneRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.phoneRemove(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authKeys.me() });
      void qc.invalidateQueries({ queryKey: authKeys.mfa() });
    },
  });
}

export function useMfaConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => authApi.mfaConfirm(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.mfa() }),
  });
}

export function useRegenerateRecovery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => authApi.mfaRegenerateRecovery(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.mfa() }),
  });
}

export function useMfaDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { method: string; code: string }) => authApi.mfaDisable(vars),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.mfa() }),
  });
}

// ── Sessions ───────────────────────────────────────────────────────

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chainId: string) => authApi.revokeSession(chainId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: authKeys.sessions() }),
  });
}

export function useLogoutAll() {
  return useMutation({
    mutationFn: async () => {
      await authApi.logoutAll();
      // The hub cleared this browser's cookies too — tear down locally.
      const { forceSignOut } = await import("@/shared/auth/session-controller");
      forceSignOut("explicit");
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (
      vars:
        | { email: string; code: string; new_password: string }
        | { challenge_id: string; token: string; new_password: string },
    ) => authApi.resetPassword(vars),
  });
}
